import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Chip,
  Button
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import PermissionsManager from './PermissionsManager';
import Chat from './Chat';
import config from '../config';

const DRAWER_WIDTH = 240;

const Dashboard = () => {
  const [showPermissions, setShowPermissions] = useState(false);
  const [userData, setUserData] = useState({
    email: '',
    role: '',
    name: '',
    username: ''
  });
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    // Get the user's data from localStorage
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    } else {
      navigate('/');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('userData');
    navigate('/');
  };

  const drawerContent = (
    <Box sx={{ width: DRAWER_WIDTH }}>
      {/* Home Button and Description */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1
        }}
      >
        <Button
          startIcon={<HomeIcon />}
          onClick={() => setShowPermissions(false)}
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            fontSize: '1.1rem',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'primary.dark',
            }
          }}
        >
          Donna.ai
        </Button>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            lineHeight: 1.2,
            pl: 1
          }}
        >
          AI Agent for Donut Naturales
        </Typography>
      </Box>

      {/* User Profile Section */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Typography 
          variant="subtitle2" 
          sx={{ 
            color: 'text.primary',
            fontWeight: 500,
          }}
        >
          {userData.name}
        </Typography>
        <Typography 
          sx={{ 
            color: 'text.secondary',
            fontSize: '0.875rem',
            wordBreak: 'break-all'
          }}
        >
          {userData.email}
        </Typography>
        <Chip
          label={userData.role}
          size="small"
          sx={{
            alignSelf: 'flex-start',
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            color: 'primary.main',
            fontWeight: 500,
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
      </Box>

      {/* Navigation List */}
      <List>
        {userData.role === 'Admin' && (
          <ListItem 
            button 
            onClick={() => setShowPermissions(!showPermissions)}
            sx={{
              mt: 1,
              color: showPermissions ? 'white' : 'inherit',
              bgcolor: showPermissions ? 'primary.main' : 'inherit',
              '&:hover': {
                bgcolor: showPermissions ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon>
              <SettingsIcon sx={{ color: showPermissions ? 'white' : 'inherit' }} />
            </ListItemIcon>
            <ListItemText primary="Manage Permissions" />
          </ListItem>
        )}
        <ListItem 
          button 
          onClick={handleLogout}
          sx={{
            mt: 1,
            '&:hover': {
              color: theme.palette.error.main,
              bgcolor: 'rgba(211, 47, 47, 0.04)',
              '& .MuiListItemIcon-root': {
                color: theme.palette.error.main,
              },
            },
          }}
        >
          <ListItemIcon>
            <LogoutIcon color="inherit" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f5f5',
        }}
      >
        {showPermissions && userData.role === 'Admin' ? (
          <PermissionsManager />
        ) : (
          <>
            {/* Avatar Header Section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'white',
              }}
            >
              <Avatar
                src={config.AVATAR_URL}
                alt="Donna AI"
                sx={{
                  width: 96,
                  height: 96,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />
            </Box>

            {/* Chat Section */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Chat />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard; 